// @flow
import { type Node } from 'react';
import styled from '@emotion/styled';
import Spinner from '@tablekit/spinner';
import StoryblokEditor from 'components/editable/StoryblokEditor';
import ComponentNotFound from './ComponentNotFound';
import type { BlokComponentType } from '../types';

const CenteredSpinner = styled(Spinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

function getFallbackElement(id: string): void | Node {
  if (typeof window !== 'undefined') {
    const existingElement = window.document.querySelector(`#${id}`);
    if (existingElement) {
      const Component = existingElement.tagName;
      return (
        <Component
          className={existingElement.getAttribute('class')}
          dangerouslySetInnerHTML={{
            __html: existingElement.innerHTML
          }}
        />
      );
    }
    return <CenteredSpinner />;
  }
  return undefined;
}

export default function renderComponent(
  blok: BlokComponentType<any>,
  staticProps: { customPath?: string, key?: string, [string]: any } = {},
  componentMap: Object
): Node {
  const { component: type } = blok;
  if (typeof componentMap[type] === 'undefined') {
    return <ComponentNotFound {...staticProps} blok={blok} />;
  }
  const Component = componentMap[type];
  const editableProps = {
    content: blok,
    key: undefined
  };
  const { key, customPath, ...passThroughProps } = staticProps;
  if (key) {
    editableProps.key = key;
  }

  // We do not use "dynamic imports" as there are a whole bunch of weird
  // bugs and issues with babel + webpack and the loader plugins which are not
  // resolvable at present
  const { asyncComponentName, AsyncComponent } = Component;
  if (asyncComponentName && AsyncComponent) {
    const id = `${asyncComponentName}_${blok._uid}`.replace(
      /[^a-z0-9_]+/gi,
      '_'
    );
    return (
      <StoryblokEditor {...editableProps} name={type}>
        <AsyncComponent
          id={id}
          componentPath={Component}
          {...passThroughProps}
          blok={blok}
          fallback={getFallbackElement(id)}
        />
      </StoryblokEditor>
    );
  }

  return (
    <StoryblokEditor {...editableProps} name={type}>
      <Component {...passThroughProps} blok={blok} />
    </StoryblokEditor>
  );
}
